import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useSnackbar } from 'notistack';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Button from '@material-ui/core/Button';
import { FormattedMessage } from 'react-intl';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import SchemaForm from '@afosto/app-components-react/SchemaForm';
import { profile as profileApi } from '@afosto/iam/api/Profile';
import translations from './translations';
import Property from '@afosto/app-components-react/Property';
import Settings from '../Settings';

const POS_RECEIPT_INFO_SETTINGS = 'afosto.tenant.iam.oauth.v1.pos';

const PosReceiptInfoSettings = ({ pathID }) => {
  const intl = useIntl();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['iam:oauth:read']);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [formSchema, setFormSchema] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const hasReadPermissions = hasPermissions();

  const uiSchema = {
    logo: {
      props: {
        label: intl.formatMessage(translations.editDrawer.logoField.label),
      },
    },
    footer_text: {
      props: {
        multiline: true,
        rows: 3,
        rowsMax: 8,
        label: intl.formatMessage(translations.editDrawer.footerTextField.label),
      },
    },
    header_text: {
      props: {
        multiline: true,
        rows: 3,
        rowsMax: 8,
        label: intl.formatMessage(translations.editDrawer.headerTextField.label),
      },
    },
  };

  const handleUpdateProfile = async fields => {
    try {
      await profileApi.updateProfile(POS_RECEIPT_INFO_SETTINGS.replace('tenant', pathID), fields);
      setSettings(fields);
      enqueueSnackbar(intl.formatMessage(translations.feedback.posReceiptInfoSettingsUpdated), {
        variant: 'success',
      });
      setIsEditing(false);
    } catch (error) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.posReceiptInfoSettingsNotUpdated), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const handleFetchProfile = async () => {
      try {
        const { data: profileData = {} } = await profileApi.getProfile(
          POS_RECEIPT_INFO_SETTINGS.replace('tenant', pathID),
        );

        if (isMounted) {
          setSettings(profileData);
        }
      } catch (error) {
        if (isMounted) {
          setSettings(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const fetchProfileFormSchema = async () => {
      try {
        const { data = {} } = await profileApi.getProfileDefinitions(
          POS_RECEIPT_INFO_SETTINGS.replace('tenant', pathID),
        );
        if (isMounted) {
          setFormSchema({
            ...data,
            properties: {
              logo: data?.properties?.logo,
              header_text: data?.properties?.header_text,
              footer_text: data?.properties?.footer_text,
            },
          });
        }
      } catch (error) {
        if (isMounted) {
          setFormSchema({});
        }
      }
    };

    if (hasReadPermissions) {
      handleFetchProfile();
      fetchProfileFormSchema();
    }

    return () => {
      isMounted = false;
    };
  }, [pathID, hasReadPermissions]);

  if (!hasReadPermissions) {
    return (
      <Settings title={intl.formatMessage(translations.title)} isLoading={false}>
        <UnauthorizedComponent />
      </Settings>
    );
  }

  return (
    <>
      <Settings title={intl.formatMessage(translations.title)} isLoading={isLoading}>
        <List dense disablePadding>
          <ListItem>
            <ListItemText
              primary={
                <>
                  <Property
                    label={intl.formatMessage(translations.editDrawer.logoField.label)}
                    value={
                      settings?.logo && (
                        <Avatar
                          variant="square"
                          imgProps={{ style: { objectFit: 'contain' } }}
                          src={settings?.logo}
                          style={{ width: 80 }}
                        />
                      )
                    }
                  />
                  <Property
                    label={intl.formatMessage(translations.editDrawer.headerTextField.label)}
                    value={
                      settings?.header_text &&
                      settings?.header_text
                        .split('\n')
                        .filter(line => line)
                        .map(line => <div key={line}>{line}</div>)
                    }
                  />
                  <Property
                    label={intl.formatMessage(translations.editDrawer.footerTextField.label)}
                    value={
                      settings?.footer_text &&
                      settings?.footer_text
                        .split('\n')
                        .filter(line => line)
                        .map(line => <div key={line}>{line}</div>)
                    }
                  />
                </>
              }
            />
            <Button onClick={() => setIsEditing(true)} variant="outlined" size="small">
              <FormattedMessage {...translations.actions.edit} />
            </Button>
          </ListItem>
        </List>
      </Settings>
      <FormDrawer
        isOpen={isEditing}
        title={intl.formatMessage(translations.editDrawer.title)}
        drawerWidth="420px"
        onClose={() => setIsEditing(false)}
      >
        {formSchema && (
          <SchemaForm
            schema={formSchema}
            uiSchema={uiSchema}
            values={settings}
            onSubmit={handleUpdateProfile}
            onCancel={() => setIsEditing(false)}
          />
        )}
      </FormDrawer>
    </>
  );
};

PosReceiptInfoSettings.propTypes = {
  pathID: PropTypes.string,
};

PosReceiptInfoSettings.defaultProps = {
  pathID: 'tenant',
};

export default PosReceiptInfoSettings;
